import React, { useState } from 'react';
import { Card, Checkbox, Typography, Input, Select, Button, Space, Tag, Flex } from 'antd';
import { SaveFilled, WarningFilled, PlusOutlined, MinusOutlined, EditFilled } from '@ant-design/icons';

const { TextArea } = Input;

export const QuestionEdit = ({ question, number, onChange, removeQuestion }) => {
  const [isEditing, setIsEditing] = useState(true);

  const setValue = (key, value) => onChange(question.id, key, value);

  return (
    <Card size="small">
      <Flex align="center" justify="space-between">
        <Space>
          <Checkbox checked={question.enabled} onChange={(e) => setValue('enabled', e.target.checked)} />
          <Typography.Title level={5} style={{ margin: 0, flex: 1 }}>
            Question {number}
          </Typography.Title>
        </Space>
        {!isEditing && (
          <Space>
            <Button size="small" type="text" icon={<EditFilled />} onClick={() => setIsEditing(true)} />
            <Button size="small" type="text" icon={<WarningFilled />} danger onClick={removeQuestion} />
          </Space>
        )}
      </Flex>
      {!isEditing ? (
        <>
          <Typography.Title level={4} style={{ margin: 0, flex: 1 }}>
            {question.question || ''}
          </Typography.Title>
          <Tag color="green">{question.type}</Tag>
          {question.required && <Tag color="red">Required</Tag>}
        </>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Card size="small">
            <Typography.Text strong>Question Text</Typography.Text>
            <TextArea
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="Enter your custom question here..."
              value={question.question}
              onChange={(e) => setValue('question', e.target.value)}
              style={{ marginTop: 8 }}
            />

            <div style={{ marginTop: 24 }}>
              <Typography.Text strong>Question Type</Typography.Text>
              <Select
                value={question.type}
                onChange={(value) => setValue('type', value)}
                style={{ width: '100%', marginTop: 8 }}
                options={[
                  { value: 'text', label: 'Text' },
                  { value: 'multiple_choice', label: 'Multiple Choice' },
                  { value: 'boolean', label: 'Yes/No' },
                  { value: 'rating', label: 'Scale Rating' },
                ]}
              />
            </div>

            {question.type === 'multiple_choice' && (
              <MultipleChoice options={question.options || []} onChange={(options) => setValue('options', options)} />
            )}

            {question.type === 'rating' && (
              <RateQuestion
                min={question.min || 1}
                max={question.max || 10}
                onChange={(range) => {
                  setValue('min', range.min);
                  setValue('max', range.max);
                }}
              />
            )}

            <Flex justify="space-between" align="center" style={{ marginTop: '1.5rem' }}>
              <Checkbox checked={question.required} onChange={(e) => setValue('required', e.target.checked)}>
                Required question
              </Checkbox>
              <Flex align="center" gap={16}>
                <Button
                  type="primary"
                  disabled={!question.question}
                  onClick={() => setIsEditing(false)}
                  icon={<SaveFilled />}>
                  Save
                </Button>
              </Flex>
            </Flex>
          </Card>
        </Space>
      )}
    </Card>
  );
};

export const MultipleChoice = ({ options = [], onChange }) => {
  const addOption = () => {
    onChange([...options, `Option ${options.length + 1}`]);
  };

  const handleInput = (index, value) => {
    const newOptions = options.map((item, idx) => (idx === index ? value : item));
    onChange(newOptions);
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    onChange(newOptions);
  };

  return (
    <div style={{ marginTop: 24 }}>
      {options.map((value, index) => (
        <Flex key={index} align="center" justify="space-between" gap={16} style={{ marginBottom: 8 }}>
          <Input
            placeholder={`Option ${options.length}`}
            value={value}
            onChange={(e) => handleInput(index, e.target.value)}
          />
          <Button icon={<MinusOutlined />} onClick={() => removeOption(index)} />
        </Flex>
      ))}
      <div style={{ marginTop: 16 }}>
        <Button type="link" icon={<PlusOutlined />} onClick={addOption}>
          Add Option
        </Button>
      </div>
    </div>
  );
};

export const RateQuestion = ({ min = 1, max = 10, onChange }) => {
  const handleMinChange = (value) => {
    const newMin = parseInt(value) || 1;
    onChange({ min: newMin, max: Math.max(newMin, max) });
  };

  const handleMaxChange = (value) => {
    const newMax = parseInt(value) || 10;
    onChange({ min: Math.min(min, newMax), max: newMax });
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Typography.Text strong>Rating Range</Typography.Text>
      <Flex align="center" gap={16} style={{ marginTop: 8 }}>
        <div style={{ flex: 1 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Min Value
          </Typography.Text>
          <Input type="number" value={min} onChange={(e) => handleMinChange(e.target.value)} style={{ marginTop: 4 }} />
        </div>
        <div style={{ flex: 1 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Max Value
          </Typography.Text>
          <Input type="number" value={max} onChange={(e) => handleMaxChange(e.target.value)} style={{ marginTop: 4 }} />
        </div>
      </Flex>
      <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
        Rating scale: {min} to {max}
      </Typography.Text>
    </div>
  );
};
